import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  attachActionFinding,
  createAction,
  getRun,
  listActionChanges,
  listActionLinks,
  listActions,
  listFindings,
  listOrganisations,
  listOriginsForFindings,
  memberDirectory,
  myAuthority,
  transitionAction,
  transitionFinding,
  unlinkActionFinding,
} from "../lib/api";
import type {
  ActionChange,
  ActionFindingLink,
  ActionRow,
  FindingKernelOrigin,
  FindingRow,
  FindingSeverity,
  MemberDirectoryEntry,
  MembershipRole,
  Organisation,
  Run,
} from "../lib/types";
import { ROLE_LABEL } from "../lib/labels";
import { useBreadcrumb } from "../shell/ContextHeader";
import { ItemCard } from "../shell/ItemCard";
import { NoNamesHint } from "./NoNamesHint";
import { StateBadge } from "./RunOverview";

// The findings & actions register (F4.4) — the console face of F4.1–F4.3.
//
// What this screen must get right (delivery review §5, all eight):
//   - attach, unlink WITH a reason, and a reason on REATTACHMENT
//   - promotion is on the results screen; its outcome (the origin badge)
//     renders here
//   - current links come from ACTIVE link rows; historical links from the
//     occurrence intervals; reasons and actors from action_changes — the
//     link table is the authoritative history, the change log the narrative
//     (linkage ruling §4.5, never the change log alone)
//   - withdrawn findings NEVER appear in an attach picker or the creation
//     picker; closed findings appear LABELLED (OQ1 as ruled)
//   - every mutating control is authority-sensitive: myAuthority()
//     fail-closed, and settled actions offer reopen, never direct edits.
//
// The database is the boundary throughout; this screen only avoids offering
// what the server would refuse, and renders the refusal verbatim when a
// stale view offers it anyway.

const SEV_COLOR: Record<FindingSeverity, string> = {
  critical: "var(--state-danger)",
  high: "var(--state-danger)",
  medium: "var(--lf-amber-deep)",
  low: "var(--text-muted)",
};

function SevBadge({ s }: { s: FindingSeverity }) {
  return (
    <span
      className="badge"
      style={{
        background: "transparent",
        border: `1px solid ${SEV_COLOR[s]}`,
        color: SEV_COLOR[s],
      }}
    >
      {s}
    </span>
  );
}

function FindingStatusBadge({ status }: { status: FindingRow["status"] }) {
  const cls =
    status === "open" ? "badge-active" : status === "closed" ? "badge-role" : "badge-archived";
  return <span className={`badge ${cls}`}>{status}</span>;
}

function ActionStatusBadge({ status }: { status: ActionRow["status"] }) {
  const cls =
    status === "open" ? "badge-active" : status === "completed" ? "badge-role" : "badge-archived";
  return <span className={`badge ${cls}`}>{status}</span>;
}

type State =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | {
      kind: "ready";
      org: Organisation;
      run: Run;
      role: MembershipRole | null;
      findings: FindingRow[];
      actions: ActionRow[];
      links: ActionFindingLink[];
      origins: FindingKernelOrigin[];
      members: MemberDirectoryEntry[];
    };

export function FindingsActionsRegister() {
  const { orgId, runId } = useParams<{ orgId: string; runId: string }>();
  const { setTrail } = useBreadcrumb();
  const [state, setState] = useState<State>({ kind: "loading" });
  const [actionError, setActionError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    if (!orgId || !runId) return;
    try {
      const [orgs, role, run, findings] = await Promise.all([
        listOrganisations(),
        myAuthority(orgId),
        getRun(runId),
        listFindings(runId),
      ]);
      const org = orgs.find((o) => o.id === orgId);
      if (!org || !run || run.organisation_id !== orgId) {
        setState({ kind: "error", message: "run not found, or you are not a member" });
        return;
      }
      const [actions, links, origins, members] = await Promise.all([
        listActions(orgId),
        listActionLinks(orgId),
        listOriginsForFindings(findings.map((f) => f.id)),
        memberDirectory(orgId),
      ]);
      setState({ kind: "ready", org, run, role, findings, actions, links, origins, members });
      setTrail([
        { label: "Clients & engagements", to: "/clients" },
        { label: org.name, to: `/clients/${org.id}` },
        { label: run.title ?? "Untitled run", to: `/clients/${org.id}/runs/${run.id}` },
        { label: "Findings & actions" },
      ]);
    } catch (e) {
      setState({ kind: "error", message: (e as Error).message });
    }
  }, [orgId, runId, setTrail]);

  useEffect(() => {
    void load();
  }, [load]);

  if (state.kind === "loading") return <p>Loading…</p>;
  if (state.kind === "error")
    return <div className="error-banner">Could not load: {state.message}</div>;

  const { run, role, findings, actions, links, origins, members } = state;
  const canWrite = role === "owner" || role === "lead_assessor" || role === "assessor";
  const runFindingIds = new Set(findings.map((f) => f.id));
  // Actions in this register: any occurrence — active OR removed — touching a
  // finding of this run. A removed-only relationship still belongs to the
  // run's history.
  const runActions = actions.filter((a) =>
    links.some((l) => l.action_id === a.id && runFindingIds.has(l.finding_id)),
  );

  async function act(fn: () => Promise<unknown>) {
    setActionError(null);
    try {
      await fn();
      await load();
    } catch (e) {
      setActionError((e as Error).message);
    }
  }

  return (
    <>
      <div style={{ display: "flex", alignItems: "baseline", gap: "0.75rem", flexWrap: "wrap" }}>
        <h1 style={{ marginBottom: 0 }}>Findings &amp; actions</h1>
        <StateBadge state={run.state} />
      </div>
      <p style={{ color: "var(--text-muted)", marginTop: "0.25rem" }}>
        {run.title ?? "Untitled run"} — the managed register. Calculated
        results stay as they are; what changes here never changes an evaluation.
      </p>
      {actionError && <div className="error-banner">{actionError}</div>}

      {/* The main action under the title (UI review slice 2). */}
      {canWrite && !creating && (
        <button className="btn" onClick={() => setCreating(true)}>
          New action
        </button>
      )}
      {canWrite && creating && (
        <NewActionForm
          findings={findings}
          members={members}
          onCreate={(input) => act(() => createAction(input))}
          onClose={() => setCreating(false)}
        />
      )}

      <FindingsCard
        findings={findings}
        origins={origins}
        canWrite={canWrite}
        onTransition={(id, to) => act(() => transitionFinding(id, to))}
      />

      <section className="card" style={{ marginTop: "1rem" }}>
        <h2 style={{ marginTop: 0 }}>Actions</h2>
        {runActions.length === 0 ? (
          <p style={{ color: "var(--text-muted)", marginBottom: 0 }}>
            No actions address this run's findings yet.
          </p>
        ) : (
          runActions.map((a) => (
            <ActionCard
              key={a.id}
              action={a}
              links={links.filter((l) => l.action_id === a.id)}
              findings={findings}
              members={members}
              canWrite={canWrite}
              act={act}
            />
          ))
        )}
      </section>

    </>
  );
}

// ---- findings -----------------------------------------------------------

function FindingsCard({
  findings,
  origins,
  canWrite,
  onTransition,
}: {
  findings: FindingRow[];
  origins: FindingKernelOrigin[];
  canWrite: boolean;
  onTransition: (id: string, to: "closed" | "withdrawn") => void;
}) {
  const originByFinding = new Map(origins.map((o) => [o.finding_id, o]));
  return (
    <section className="card" style={{ marginTop: "1rem" }}>
      <h2 style={{ marginTop: 0 }}>Findings</h2>
      {findings.length === 0 ? (
        <p style={{ color: "var(--text-muted)", marginBottom: 0 }}>
          No managed findings on this run yet. Promote engine findings from
          the results screen, or record one from reviewed material.
        </p>
      ) : (
        <div>
          {findings.map((f) => {
            const origin = originByFinding.get(f.id);
            return (
              <ItemCard
                key={f.id}
                id={`F-${f.seq}`}
                title={f.title}
                tags={
                  <>
                    <SevBadge s={f.severity} />
                    {origin && (
                      <span
                        className="badge badge-role"
                        title={`Promoted from engine finding ${origin.kernel_finding_key} (promotion ${origin.promotion_generation}). Original severity ${origin.original_severity}; original wording retained in provenance.`}
                      >
                        from results
                      </span>
                    )}
                  </>
                }
                state={<FindingStatusBadge status={f.status} />}
                facts={
                  f.block_key
                    ? `${f.block_key}${f.criterion_key ? `: ${f.criterion_key}` : ""}`
                    : undefined
                }
                note={f.description ?? undefined}
              >
                {canWrite && f.status === "open" && (
                  <span style={{ display: "flex", gap: "0.4rem" }}>
                    <button className="btn btn-secondary" onClick={() => onTransition(f.id, "closed")}>
                      Close
                    </button>
                    <button
                      className="btn btn-secondary"
                      title="Withdraw: this finding should not stand as a valid expression. It cannot receive new action links afterwards; existing links survive as history."
                      onClick={() => {
                        if (window.confirm("Withdraw this finding? Withdrawal is terminal and it can no longer receive action links."))
                          onTransition(f.id, "withdrawn");
                      }}
                    >
                      Withdraw
                    </button>
                  </span>
                )}
              </ItemCard>
            );
          })}
        </div>
      )}
    </section>
  );
}

// ---- one action ---------------------------------------------------------

function ActionCard({
  action,
  links,
  findings,
  members,
  canWrite,
  act,
}: {
  action: ActionRow;
  links: ActionFindingLink[];
  findings: FindingRow[];
  members: MemberDirectoryEntry[];
  canWrite: boolean;
  act: (fn: () => Promise<unknown>) => Promise<void>;
}) {
  const [changes, setChanges] = useState<ActionChange[] | null>(null);
  const [unlinkFor, setUnlinkFor] = useState<string | null>(null); // link id
  const [unlinkReason, setUnlinkReason] = useState("");
  const [lifecycleReason, setLifecycleReason] = useState("");

  const findingById = new Map(findings.map((f) => [f.id, f]));
  const active = links.filter((l) => l.removed_at === null);
  const open = action.status === "open";
  const owner = members.find((m) => m.user_id === action.owner_id);

  async function loadHistory() {
    if (changes === null) setChanges(await listActionChanges(action.id));
  }

  return (
    <ItemCard
      id={`A-${action.seq}`}
      title={action.title}
      tags={<SevBadge s={action.priority} />}
      state={<ActionStatusBadge status={action.status} />}
      facts={
        <>
          {owner ? owner.email : action.owner_id}
          {action.due_date && ` · due ${action.due_date}`}
        </>
      }
    >
      {action.required_outcome && (
        <p style={{ margin: "0.35rem 0 0", color: "var(--text-muted)", fontSize: "var(--lf-body-size)" }}>
          Done means: {action.required_outcome}
        </p>
      )}
      {action.status === "completed" && action.completion_statement && (
        <p style={{ margin: "0.35rem 0 0", fontSize: "var(--lf-body-size)" }}>
          Completed: {action.completion_statement}
        </p>
      )}

      <div style={{ marginTop: "0.6rem" }}>
        <span style={{ color: "var(--text-muted)", fontSize: "var(--lf-meta-size)" }}>Addresses</span>
        <ul style={{ margin: "0.25rem 0 0", padding: 0, listStyle: "none" }}>
          {active.map((l) => {
            const f = findingById.get(l.finding_id);
            return (
              <li
                key={l.id}
                style={{ display: "flex", gap: "0.5rem", alignItems: "baseline", flexWrap: "wrap", padding: "0.2rem 0" }}
              >
                <span style={{ flex: 1 }}>
                  {f ? (
                    <>
                      <code>F-{f.seq}</code> {f.title}
                      {f.status !== "open" && (
                        <span style={{ color: "var(--text-muted)" }}> ({f.status})</span>
                      )}
                    </>
                  ) : (
                    <span style={{ color: "var(--text-muted)" }}>
                      finding outside this run ({l.finding_id.slice(0, 8)}…)
                    </span>
                  )}
                </span>
                {canWrite && open && unlinkFor !== l.id && (
                  <button
                    className="btn btn-secondary"
                    disabled={active.length <= 1}
                    title={
                      active.length <= 1
                        ? "An action addresses at least one finding — attach another first, or cancel the action."
                        : "Remove this finding from the action's scope (a reason is required)."
                    }
                    onClick={() => {
                      setUnlinkFor(l.id);
                      setUnlinkReason("");
                    }}
                  >
                    Unlink
                  </button>
                )}
                {unlinkFor === l.id && (
                  <span style={{ display: "flex", gap: "0.4rem", alignItems: "center", flexWrap: "wrap" }}>
                    <input
                      placeholder="Reason (required)"
                      value={unlinkReason}
                      onChange={(e) => setUnlinkReason(e.target.value)}
                      aria-label="Unlink reason"
                    />
                    <NoNamesHint />
                    <button
                      className="btn"
                      disabled={unlinkReason.trim() === ""}
                      onClick={() =>
                        void act(async () => {
                          await unlinkActionFinding(l.id, unlinkReason.trim());
                          setUnlinkFor(null);
                        })
                      }
                    >
                      Confirm unlink
                    </button>
                    <button className="btn btn-secondary" onClick={() => setUnlinkFor(null)}>
                      Keep
                    </button>
                  </span>
                )}
              </li>
            );
          })}
        </ul>
        {canWrite && open && (
          <AttachControl action={action} links={links} findings={findings} act={act} />
        )}
        {canWrite && !open && (
          <p style={{ color: "var(--text-muted)", fontSize: "var(--lf-meta-size)", margin: "0.4rem 0 0" }}>
            A {action.status} action's scope is settled — reopen it (with a
            reason) before changing which findings it addresses.
          </p>
        )}
      </div>

      {canWrite && (
        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap", marginTop: "0.6rem" }}>
          {open && (
            <>
              <input
                placeholder="Completion statement / cancellation reason"
                value={lifecycleReason}
                onChange={(e) => setLifecycleReason(e.target.value)}
                aria-label="Lifecycle reason"
                style={{ minWidth: "18rem" }}
              />
              <NoNamesHint />
              <button
                className="btn"
                onClick={() =>
                  void act(async () => {
                    await transitionAction(action.id, "completed", lifecycleReason.trim() || null);
                    setLifecycleReason("");
                  })
                }
              >
                Complete
              </button>
              <button
                className="btn btn-danger"
                disabled={lifecycleReason.trim() === ""}
                title="Cancellation is terminal and requires a reason; a replacement intervention is a new action."
                onClick={() =>
                  void act(async () => {
                    await transitionAction(action.id, "cancelled", lifecycleReason.trim());
                    setLifecycleReason("");
                  })
                }
              >
                Cancel action
              </button>
            </>
          )}
          {action.status === "completed" && (
            <>
              <input
                placeholder="Reopening reason (required)"
                value={lifecycleReason}
                onChange={(e) => setLifecycleReason(e.target.value)}
                aria-label="Reopening reason"
                style={{ minWidth: "18rem" }}
              />
              <NoNamesHint />
              <button
                className="btn btn-secondary"
                disabled={lifecycleReason.trim() === ""}
                onClick={() =>
                  void act(async () => {
                    await transitionAction(action.id, "open", lifecycleReason.trim());
                    setLifecycleReason("");
                  })
                }
              >
                Reopen
              </button>
            </>
          )}
          {action.status === "cancelled" && (
            <span style={{ color: "var(--text-muted)", fontSize: "var(--lf-body-size)" }}>
              Cancelled is terminal — continuing work needs a new action.
            </span>
          )}
        </div>
      )}

      <details style={{ marginTop: "0.6rem" }} onToggle={(e) => e.currentTarget.open && void loadHistory()}>
        <summary style={{ cursor: "pointer", color: "var(--text-muted)", fontSize: "var(--lf-body-size)" }}>
          Link history &amp; audit trail
        </summary>
        <div style={{ fontSize: "var(--lf-body-size)", marginTop: "0.4rem" }}>
          <p style={{ color: "var(--text-muted)", margin: 0 }}>
            Every relationship occurrence, including removed ones — this table
            is the authoritative history; the audit trail below carries the
            narrative.
          </p>
          <ul style={{ margin: "0.35rem 0 0", paddingLeft: "1.1rem" }}>
            {links.map((l) => {
              const f = findingById.get(l.finding_id);
              return (
                <li key={l.id}>
                  {f?.title ?? l.finding_id}:{" "}
                  {new Date(l.created_at).toLocaleString()} →{" "}
                  {l.removed_at ? (
                    <>
                      {new Date(l.removed_at).toLocaleString()}{" "}
                      <span style={{ color: "var(--text-muted)" }}>
                        (removed: {l.removed_reason})
                      </span>
                    </>
                  ) : (
                    <span style={{ color: "var(--state-ok)" }}>active</span>
                  )}
                </li>
              );
            })}
          </ul>
          {changes && changes.length > 0 && (
            <ul style={{ margin: "0.5rem 0 0", paddingLeft: "1.1rem", color: "var(--text-muted)" }}>
              {changes.map((c) => (
                <li key={c.id}>
                  {new Date(c.changed_at).toLocaleString()} — {c.changed_field.replaceAll("_", " ")}:{" "}
                  {c.old_value ?? "—"} → {c.new_value ?? "—"}
                  {c.reason && <> ({c.reason})</>}
                </li>
              ))}
            </ul>
          )}
          {changes && changes.length === 0 && (
            <p style={{ color: "var(--text-muted)", margin: "0.4rem 0 0" }}>
              No audited changes yet.
            </p>
          )}
        </div>
      </details>
    </ItemCard>
  );
}

// ---- attach -------------------------------------------------------------

function AttachControl({
  action,
  links,
  findings,
  act,
}: {
  action: ActionRow;
  links: ActionFindingLink[];
  findings: FindingRow[];
  act: (fn: () => Promise<unknown>) => Promise<void>;
}) {
  const [chosen, setChosen] = useState("");
  const [reason, setReason] = useState("");

  const activelyLinked = new Set(
    links.filter((l) => l.removed_at === null).map((l) => l.finding_id),
  );
  // OQ1 as ruled: open and closed findings may be attached, closed ones
  // labelled; WITHDRAWN findings are excluded from the picker entirely —
  // new scope is never built against a withdrawn expression.
  const candidates = findings.filter(
    (f) => (f.status === "open" || f.status === "closed") && !activelyLinked.has(f.id),
  );
  const isReattach =
    chosen !== "" &&
    links.some((l) => l.finding_id === chosen && l.removed_at !== null);
  const reasonMissing = isReattach && reason.trim() === "";

  if (candidates.length === 0) return null;
  return (
    <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap", marginTop: "0.45rem" }}>
      <select value={chosen} onChange={(e) => setChosen(e.target.value)} aria-label="Attach finding">
        <option value="">Attach finding…</option>
        {candidates.map((f) => (
          <option key={f.id} value={f.id}>
            {`F-${f.seq} ${f.title}`}
            {f.status === "closed" ? " (closed)" : ""}
          </option>
        ))}
      </select>
      <input
        placeholder={isReattach ? "Reason (required for reattachment)" : "Reason (optional)"}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        aria-label="Attach reason"
      />
      <NoNamesHint />
      <button
        className="btn"
        disabled={chosen === "" || reasonMissing}
        title={
          reasonMissing
            ? "This finding was previously unlinked from this action; restoring the relationship requires a reason."
            : undefined
        }
        onClick={() =>
          void act(async () => {
            await attachActionFinding(action.id, chosen, reason.trim() || null);
            setChosen("");
            setReason("");
          })
        }
      >
        Attach
      </button>
      {isReattach && (
        <span style={{ color: "var(--lf-amber-deep)", fontSize: "var(--lf-meta-size)" }}>
          Reattachment reverses an earlier deliberate unlink — say why.
        </span>
      )}
    </div>
  );
}

// ---- create -------------------------------------------------------------

function NewActionForm({
  findings,
  members,
  onCreate,
  onClose,
}: {
  findings: FindingRow[];
  members: MemberDirectoryEntry[];
  onClose: () => void;
  onCreate: (input: {
    title: string;
    priority: FindingSeverity;
    ownerId: string;
    findingIds: string[];
    requiredOutcome?: string | null;
    dueDate?: string | null;
  }) => Promise<void>;
}) {
  const [title, setTitle] = useState("");
  const [priority, setPriority] = useState<FindingSeverity>("medium");
  const [ownerId, setOwnerId] = useState("");
  const [outcome, setOutcome] = useState("");
  const [due, setDue] = useState("");
  const [picked, setPicked] = useState<Set<string>>(new Set());

  // Same OQ1 rule at creation: open + closed (labelled), never withdrawn.
  const candidates = findings.filter((f) => f.status === "open" || f.status === "closed");
  // The owner answers for the intervention; a viewer cannot (0011 §3).
  const eligibleOwners = members.filter((m) => m.member_role !== "viewer");

  const ready = title.trim() !== "" && ownerId !== "" && picked.size > 0;

  return (
    <section className="card" style={{ marginTop: "1rem" }}>
      <h2 style={{ marginTop: 0 }}>New action</h2>
      {candidates.length === 0 ? (
        <p style={{ color: "var(--text-muted)", marginBottom: 0 }}>
          An action addresses at least one finding — record or promote a
          finding first.{" "}
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
        </p>
      ) : (
        <div style={{ display: "grid", gap: "0.6rem", maxWidth: "44rem" }}>
          <input
            placeholder="What will be done"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            aria-label="Action title"
          />
          <NoNamesHint />
          <input
            placeholder="What done looks like (optional)"
            value={outcome}
            onChange={(e) => setOutcome(e.target.value)}
            aria-label="Required outcome"
          />
          <NoNamesHint />
          <div style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap" }}>
            <select value={priority} onChange={(e) => setPriority(e.target.value as FindingSeverity)} aria-label="Priority">
              {(["critical", "high", "medium", "low"] as const).map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
            <select value={ownerId} onChange={(e) => setOwnerId(e.target.value)} aria-label="Owner">
              <option value="">Owner…</option>
              {eligibleOwners.map((m) => (
                <option key={m.user_id} value={m.user_id}>
                  {m.email} ({ROLE_LABEL[m.member_role]})
                </option>
              ))}
            </select>
            <input type="date" value={due} onChange={(e) => setDue(e.target.value)} aria-label="Due date" />
          </div>
          <fieldset style={{ border: "1px solid var(--border, #e3e3e3)", borderRadius: "var(--radius)", padding: "0.5rem 0.75rem" }}>
            <legend style={{ color: "var(--text-muted)", fontSize: "var(--lf-body-size)" }}>
              Addresses (at least one)
            </legend>
            {candidates.map((f) => (
              <label key={f.id} style={{ display: "block", padding: "0.15rem 0" }}>
                <input
                  type="checkbox"
                  checked={picked.has(f.id)}
                  onChange={(e) => {
                    const next = new Set(picked);
                    if (e.target.checked) next.add(f.id);
                    else next.delete(f.id);
                    setPicked(next);
                  }}
                />{" "}
                <code>F-{f.seq}</code> {f.title}
                {f.status === "closed" && <span style={{ color: "var(--text-muted)" }}> (closed)</span>}
              </label>
            ))}
          </fieldset>
          <div>
            <button
              className="btn"
              disabled={!ready}
              onClick={() =>
                void onCreate({
                  title: title.trim(),
                  priority,
                  ownerId,
                  findingIds: [...picked],
                  requiredOutcome: outcome.trim() || null,
                  dueDate: due || null,
                }).then(() => {
                  setTitle("");
                  setOutcome("");
                  setDue("");
                  setOwnerId("");
                  setPicked(new Set());
                  onClose();
                })
              }
            >
              Create action
            </button>{" "}
            <button className="btn btn-secondary" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
